<template>
  <BaseModal :value="show" @input="$emit('close')" class="stripe-payment-modal">
    <template v-slot:header>
      <div class="text-xl font-bold">Process Stripe Payment</div>
    </template>
    <div style="max-width: 400px;" class="p-6 mx-auto">
      <!-- Payment Summary -->
      <div v-if="!paymentCompleted" class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 class="font-semibold mb-2">Payment Details</h3>
        <div class="flex justify-between text-sm">
          <span>Order #{{ orderNumber }}</span>
          <span class="font-bold">${{ amountToDisplay }}</span>
        </div>
      </div>

      <!-- Confirmation Screen for Saved Card -->
      <div v-if="showConfirmation && !paymentCompleted" class="py-8">
        <div class="text-center mb-6">
          <h3 class="text-lg font-semibold mb-2">Confirm Payment</h3>
          <p class="text-sm text-gray-600">
            Review the details before processing
          </p>
        </div>

        <!-- Selected Payment Method -->
        <div class="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <div class="text-sm font-semibold text-gray-700 mb-2">
            Payment Method:
          </div>
          <div class="flex items-center gap-3">
            <span class="text-3xl">💳</span>
            <div>
              <div class="font-semibold text-lg">
                {{ selectedPaymentMethod.brand.toUpperCase() }} ••••
                {{ selectedPaymentMethod.last4 }}
              </div>
              <div class="text-sm text-gray-600">
                Expires {{ selectedPaymentMethod.exp_month }}/{{
                  selectedPaymentMethod.exp_year
                }}
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Details -->
        <div
          v-if="pendingPaymentData"
          class="mb-6 p-4 bg-gray-50 rounded-lg space-y-2"
        >
          <div class="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span
              >${{
                (
                  pendingPaymentData.amount -
                  parseFloat(pendingPaymentData.shipping?.price || 0) -
                  parseFloat(pendingPaymentData.tax || 0) -
                  parseFloat(pendingPaymentData.core || 0)
                ).toFixed(2)
              }}</span
            >
          </div>
          <div
            v-if="pendingPaymentData.shipping"
            class="flex justify-between text-sm"
          >
            <span>Shipping:</span>
            <span>${{ pendingPaymentData.shipping.price }}</span>
          </div>
          <div
            v-if="pendingPaymentData.tax"
            class="flex justify-between text-sm"
          >
            <span>Tax:</span>
            <span>${{ pendingPaymentData.tax }}</span>
          </div>
          <div
            v-if="
              pendingPaymentData.core && parseFloat(pendingPaymentData.core) > 0
            "
            class="flex justify-between text-sm"
          >
            <span>Core:</span>
            <span>${{ pendingPaymentData.core }}</span>
          </div>
          <div class="border-t border-gray-300 pt-2 mt-2" />
          <div class="flex justify-between font-bold text-lg">
            <span>Total:</span>
            <span>${{ pendingPaymentData.amount.toFixed(2) }}</span>
          </div>
        </div>

        <div
          v-if="paymentError"
          class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
        >
          {{ paymentError }}
        </div>

        <!-- Confirm/Cancel Buttons -->
        <div class="flex gap-3">
          <button
            @click="confirmExistingCardPayment"
            :disabled="processingPayment"
            class="flex-1 bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="processingPayment">Processing...</span>
            <span v-else
              >Confirm & Pay ${{
                pendingPaymentData
                  ? pendingPaymentData.amount.toFixed(2)
                  : amount
              }}</span
            >
          </button>
          <button
            @click="cancelConfirmation"
            :disabled="processingPayment"
            class="px-6 py-3 border border-gray-300 rounded-md font-semibold hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Payment Method Selection (Before Loading) -->
      <div
        v-if="
          !paymentIntentInitialized && !paymentCompleted && !showConfirmation
        "
        class="py-8"
      >
        <div class="text-center mb-6">
          <h3 class="text-lg font-semibold mb-2">Select Payment Method</h3>
          <p class="text-sm text-gray-600">
            Choose a saved card or enter a new one
          </p>
        </div>

        <!-- Loading Saved Cards -->
        <div v-if="loadingSavedCards" class="text-center py-4">
          <BaseMiniSpinner />
          <p class="text-sm text-gray-600 mt-2">Loading saved cards...</p>
        </div>

        <!-- Saved Payment Methods -->
        <div v-else-if="savedPaymentMethods.length > 0" class="space-y-3 mb-4">
          <div class="text-sm font-semibold text-gray-700 mb-2">
            Saved Cards:
          </div>
          <button
            v-for="method in savedPaymentMethods"
            :key="method.id"
            @click="useExistingCard(method)"
            class="w-full border-2 border-gray-300 bg-white px-4 py-3 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="text-2xl">💳</span>
                <div>
                  <div class="font-semibold">
                    {{ method.brand.toUpperCase() }} ••••
                    {{ method.last4 }}
                  </div>
                  <div class="text-xs text-gray-500">
                    Expires {{ method.exp_month }}/{{ method.exp_year }}
                  </div>
                </div>
              </div>
              <span class="text-blue-600 text-sm font-semibold"
                >Use This Card</span
              >
            </div>
          </button>
          <div class="border-t border-gray-300 my-4" />
        </div>

        <!-- New Card Options -->
        <div class="space-y-3">
          <button
            @click="initializeWithSaveCard(true)"
            class="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700"
          >
            <span v-if="savedPaymentMethods.length > 0"
              >+ Add New Card & Save</span
            >
            <span v-else>Add New Card & Save for Future</span>
          </button>
          <button
            @click="initializeWithSaveCard(false)"
            class="w-full bg-gray-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-gray-700"
          >
            <span v-if="savedPaymentMethods.length > 0"
              >+ Add New Card (One-Time)</span
            >
            <span v-else>Add New Card (One-Time Only)</span>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingScript" class="flex justify-center items-center py-12">
        <BaseMiniSpinner />
        <span class="ml-2">Initializing payment...</span>
      </div>

      <!-- Payment Form -->
      <div
        v-if="
          paymentIntentId &&
            !verifyingPayment &&
            !paymentCompleted &&
            !showConfirmation
        "
      >
        <form id="payment-form" @submit.prevent="completePayment">
          <div id="payment-element" class="mb-4" />

          <div
            v-if="paymentError"
            class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
          >
            {{ paymentError }}
          </div>

          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="processingPayment"
              class="flex-1 bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="processingPayment">Processing...</span>
              <span v-else>Pay ${{ amountToDisplay }}</span>
            </button>
            <button
              type="button"
              @click="$emit('close')"
              :disabled="processingPayment"
              class="px-6 py-3 border border-gray-300 rounded-md font-semibold hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <!-- Verifying Payment -->
      <div v-if="verifyingPayment" class="text-center py-12">
        <BaseMiniSpinner />
        <p class="mt-4 text-gray-600">Verifying payment...</p>
      </div>

      <!-- Payment Completed -->
      <div v-if="paymentCompleted" class="text-center py-12">
        <div class="text-6xl mb-4">✓</div>
        <h3 class="text-2xl font-bold text-green-600 mb-2">
          Payment Successful!
        </h3>
        <p class="text-gray-600">Payment has been processed successfully.</p>
        <button
          @click="$emit('close')"
          class="mt-6 bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700"
        >
          Close
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";

export default {
  name: "StripePaymentModal",
  props: {
    show: {
      type: Boolean,
      required: true
    },
    orderNumber: {
      type: [String, Number],
      required: true
    },
    amount: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      stripe: null,
      elements: null,
      clientSecret: "",
      paymentIntentId: "",
      customerEmail: "",
      billingAddress: null,
      loadingScript: false,
      elementsReady: false,
      processingPayment: false,
      verifyingPayment: false,
      paymentCompleted: false,
      paymentError: null,
      saveCard: false,
      paymentIntentInitialized: false,
      loadingSavedCards: false,
      savedPaymentMethods: [],
      selectedPaymentMethodId: null,
      selectedPaymentMethod: null,
      showConfirmation: false,
      pendingPaymentData: null,
      displayAmount: null,
      initialPaymentIntentData: null // Store the initial payment intent to reuse
    };
  },
  computed: {
    amountToDisplay() {
      // Use the amount from payment intent response if available, otherwise use prop
      return this.displayAmount !== null ? this.displayAmount : this.amount;
    }
  },
  watch: {
    show(val) {
      if (val) {
        // Reset ALL state when modal opens (even if payment was completed)
        this.resetState();
        // Load saved payment methods
        this.loadSavedPaymentMethods();
      }
    }
  },
  mounted() {
    if (this.show) {
      this.loadSavedPaymentMethods();
    }
  },
  methods: {
    resetState() {
      // Reset all state variables to their initial values
      this.clientSecret = "";
      this.paymentIntentId = "";
      this.customerEmail = "";
      this.billingAddress = null;
      this.loadingScript = false;
      this.elementsReady = false;
      this.processingPayment = false;
      this.verifyingPayment = false;
      this.paymentCompleted = false;
      this.paymentError = null;
      this.saveCard = false;
      this.paymentIntentInitialized = false;
      this.loadingSavedCards = false;
      this.savedPaymentMethods = [];
      this.selectedPaymentMethodId = null;
      this.selectedPaymentMethod = null;
      this.showConfirmation = false;
      this.pendingPaymentData = null;
      this.displayAmount = null;
      this.initialPaymentIntentData = null;

      // Unmount Stripe elements if they exist
      if (this.elements) {
        this.elements = null;
      }
    },
    async loadSavedPaymentMethods() {
      // Create ONE initial payment intent to get customer email and billing info
      // This will be reused when user clicks "Add New Card" if saveCard preference matches
      this.loadingSavedCards = true;
      try {
        // Create initial payment intent with saveCard: false (most common case)
        const response = await axios.post(
          process.env.VUE_APP_WEB_API +
            "/stripex/payment_intent_existing_order",
          {
            order_number: this.orderNumber,
            amount: parseFloat(this.amount),
            saveCard: false, // Default - will create new one if user chooses to save
            isAdmin: true
          },
          {
            headers: {
              Authorization: this.$store.getters["auth/currentToken"]
            }
          }
        );

        const email = response.data.email;

        // Store this initial payment intent data for reuse
        this.initialPaymentIntentData = {
          secret: response.data.secret,
          paymentIntentId: response.data.paymentIntentId,
          amount: response.data.amount,
          email: response.data.email,
          address: response.data.address,
          saveCard: false
        };

        this.customerEmail = email;
        this.billingAddress = response.data.address;
        this.displayAmount = response.data.amount;

        if (email) {
          // Now load saved payment methods for this email
          const savedMethodsResponse = await axios.post(
            process.env.VUE_APP_WEB_API + "/stripex/saved_payment_methods",
            {
              email: email
            },
            {
              headers: {
                Authorization: this.$store.getters["auth/currentToken"]
              }
            }
          );

          this.savedPaymentMethods =
            savedMethodsResponse.data.payment_methods || [];
        }
      } catch (error) {
        console.error("Error loading saved payment methods:", error);
        this.savedPaymentMethods = [];
      } finally {
        this.loadingSavedCards = false;
      }
    },
    async useExistingCard(paymentMethod) {
      this.selectedPaymentMethodId = paymentMethod.id;
      this.selectedPaymentMethod = paymentMethod;
      this.saveCard = false; // Already saved
      this.loadingScript = true;

      try {
        // Create payment intent with existing payment method
        const response = await axios.post(
          process.env.VUE_APP_WEB_API +
            "/stripex/payment_intent_existing_order",
          {
            order_number: this.orderNumber,
            amount: parseFloat(this.amount),
            saveCard: false,
            payment_method_id: paymentMethod.id
          }
        );

        // Store payment data for confirmation
        this.pendingPaymentData = response.data;
        this.clientSecret = response.data.secret;
        this.paymentIntentId = response.data.paymentIntentId;
        this.displayAmount = response.data.amount;

        // Load Stripe if not already loaded
        if (!this.stripe) {
          this.stripe = await loadStripe(
            process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
          );
        }

        // Show confirmation screen
        this.showConfirmation = true;
        this.paymentIntentInitialized = true;
      } catch (error) {
        console.error("Error preparing existing card payment:", error);
        this.paymentError =
          error.response?.data?.message ||
          error.message ||
          "Failed to prepare payment";
      } finally {
        this.loadingScript = false;
      }
    },
    async confirmExistingCardPayment() {
      this.processingPayment = true;
      this.verifyingPayment = true;
      this.showConfirmation = false;

      try {
        // Confirm the payment with Stripe
        const result = await this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: this.selectedPaymentMethodId
        });

        if (result.error) {
          throw new Error(result.error.message);
        }

        // Verify payment on server
        await this.verifyPaymentServer();
      } catch (error) {
        console.error("Error confirming existing card payment:", error);
        this.paymentError = error.message || "Failed to process payment";
        this.processingPayment = false;
        this.verifyingPayment = false;
        this.showConfirmation = true; // Show confirmation again to retry
      }
    },
    cancelConfirmation() {
      this.showConfirmation = false;
      this.paymentIntentInitialized = false;
      this.selectedPaymentMethodId = null;
      this.selectedPaymentMethod = null;
      this.pendingPaymentData = null;
      this.clientSecret = "";
      this.paymentIntentId = "";
    },
    async initializeWithSaveCard(shouldSaveCard) {
      this.saveCard = shouldSaveCard;
      this.paymentIntentInitialized = true;
      this.selectedPaymentMethodId = null;
      await this.initializePayment();
    },
    async initializePayment() {
      try {
        // Load Stripe
        if (!this.stripe) {
          this.loadingScript = true;
          this.stripe = await loadStripe(
            process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
          );
        }

        // Create payment intent with saveCard preference
        await this.createPaymentIntent();

        // Initialize Stripe Elements
        if (this.clientSecret && this.stripe) {
          console.log(
            "Initializing Stripe Elements with secret:",
            this.clientSecret
          );

          const appearance = { theme: "stripe" };

          // Create elements with default values from payment intent response
          const elementsOptions = {
            appearance,
            clientSecret: this.clientSecret
          };

          // Add default values if available
          if (this.customerEmail || this.billingAddress) {
            elementsOptions.defaultValues = {};

            if (this.customerEmail) {
              elementsOptions.defaultValues.billingDetails = {
                email: this.customerEmail
              };

              if (this.billingAddress) {
                elementsOptions.defaultValues.billingDetails.address = {
                  postal_code: this.billingAddress.postal_code,
                  country: this.billingAddress.country
                };

                if (this.billingAddress.name) {
                  elementsOptions.defaultValues.billingDetails.name = this.billingAddress.name;
                }
              }
            }
          }

          this.elements = this.stripe.elements(elementsOptions);

          // Set loading to false first so DOM renders
          this.loadingScript = false;

          // Wait for DOM to be ready with multiple ticks to ensure render
          await this.$nextTick();
          await this.$nextTick();

          // Create and mount card element
          const cardElement = this.elements.create("card", {
            disableLink: true
          });
          const cardContainer = document.getElementById("payment-element");
          if (cardContainer) {
            cardElement.mount("#payment-element");
            console.log("Card element mounted");
            this.elementsReady = true;
          } else {
            console.error("Card element container not found");
          }
        } else {
          console.error("Missing clientSecret or stripe:", {
            clientSecret: this.clientSecret,
            stripe: !!this.stripe
          });
          this.loadingScript = false;
        }
      } catch (error) {
        console.error("Error initializing payment:", error);
        this.paymentError =
          error.response?.data?.message ||
          error.message ||
          "Failed to initialize payment";
        this.loadingScript = false;
      }
    },
    async createPaymentIntent() {
      try {
        // Check if we need to create a new payment intent
        // Only create one if saveCard preference has changed or we don't have one yet
        const needsNewIntent =
          !this.initialPaymentIntentData ||
          this.initialPaymentIntentData.saveCard !== this.saveCard;

        if (needsNewIntent) {
          const payload = {
            order_number: this.orderNumber,
            amount: parseFloat(this.amount),
            saveCard: this.saveCard,
            isAdmin: true
          };

          // Request to minimize 3DS challenges when saving card
          if (this.saveCard) {
            payload.request_three_d_secure = "any"; // Server should set this on payment intent
          }

          const response = await axios.post(
            process.env.VUE_APP_WEB_API +
              "/stripex/payment_intent_existing_order",
            payload,
            {
              headers: {
                Authorization: this.$store.getters["auth/currentToken"]
              }
            }
          );

          // Store the payment intent data for potential reuse
          this.initialPaymentIntentData = {
            secret: response.data.secret,
            paymentIntentId: response.data.paymentIntentId,
            amount: response.data.amount,
            email: response.data.email,
            address: response.data.address,
            saveCard: this.saveCard
          };
        }

        // Use the stored payment intent data
        this.clientSecret = this.initialPaymentIntentData.secret;
        this.paymentIntentId = this.initialPaymentIntentData.paymentIntentId;
        this.displayAmount = this.initialPaymentIntentData.amount;

        // Store email and address from response for prefilling
        if (!this.customerEmail) {
          this.customerEmail = this.initialPaymentIntentData.email;
        }
        if (!this.billingAddress) {
          this.billingAddress = this.initialPaymentIntentData.address;
        }
      } catch (error) {
        console.error("Error creating payment intent:", error);
        throw error;
      }
    },
    async completePayment() {
      if (this.processingPayment) return;

      this.paymentError = null;
      this.processingPayment = true;

      try {
        // Get the card element
        const cardElement = this.elements.getElement("card");

        // Prepare confirmation options
        // Note: 3DS settings are configured on the server when creating the Payment Intent
        // We cannot override them here on the client side
        const confirmOptions = {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: this.customerEmail,
              name: this.billingAddress?.name || "",
              address: {
                postal_code: this.billingAddress?.postal_code || "",
                country: this.billingAddress?.country || ""
              }
            }
          }
        };

        // Confirm card payment with billing details
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          confirmOptions
        );

        if (error) {
          this.paymentError = error.message;
          this.processingPayment = false;
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
          // Verify payment on server
          await this.verifyPaymentServer();
        } else {
          this.paymentError = "Payment was not completed. Please try again.";
          this.processingPayment = false;
        }
      } catch (error) {
        console.error("Payment error:", error);
        this.paymentError = error.message || "An error occurred during payment";
        this.processingPayment = false;
      }
    },
    async verifyPaymentServer() {
      this.verifyingPayment = true;
      this.processingPayment = false;

      const maxAttempts = 30; // Maximum polling attempts (30 seconds)
      const pollInterval = 1000; // Poll every 1 second
      let attempts = 0;

      try {
        while (attempts < maxAttempts) {
          attempts++;
          console.log(
            `Polling payment verification (attempt ${attempts}/${maxAttempts})`
          );

          const response = await axios.get(
            process.env.VUE_APP_WEB_API +
              `/stripex/verify_payment/${this.paymentIntentId}`
          );

          // Check if payment was successful
          if (response.data.status === "succeeded") {
            this.paymentCompleted = true;
            this.$toast.open({
              message: "Payment processed successfully",
              type: "success",
              position: "top-right"
            });

            // Emit success event with payment data
            this.$emit("payment-success", {
              paymentIntentId: this.paymentIntentId,
              amount: this.amount,
              orderNumber: this.orderNumber,
              orderData: response.data.order
            });

            this.verifyingPayment = false;
            return; // Exit the polling loop
          }

          // Check if payment failed
          if (response.data.status === "failed") {
            this.paymentError =
              response.data.message ||
              "Payment verification failed. Please contact support.";
            this.verifyingPayment = false;
            return;
          }

          // If status is "pending", continue polling
          if (attempts < maxAttempts) {
            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
        }

        // If we've exhausted all attempts
        this.paymentError =
          "Payment verification timed out. Please check your order status.";
      } catch (error) {
        console.error("Error verifying payment:", error);
        this.paymentError =
          error.response?.data?.message || "Failed to verify payment";
      } finally {
        this.verifyingPayment = false;
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
.stripe-payment-modal {
  .modal-content {
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
  }
}

#payment-form {
  width: 100%;
  max-width: 500px;
}

#payment-element {
  margin-bottom: 1rem;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: white;
}
</style>
